/**
 * Itinerary PDF content — cover plus one expanded day section per calendar day.
 * Tile order matches iOS PDFDayExportBuilder.
 */

import type { ItineraryItem, Trip, TripHotelStay } from '../types/client';
import { dayViewEn, tripDetailEn } from '../content/trips.en';
import { allTripHotelStays } from './cruiseLodgingCityVisitFilter';
import { groupDayItemsByCity, shouldShowCitySectionHeaders } from './groupDayItemsByCity';
import {
  hotelStayIsCheckInOrOutOn,
  hotelStayIsOvernightOn,
  hotelStaysForDate,
} from './hotelStayHelpers';
import { compareItemTimes, formatItemTimeDisplay, itemTimeToMinutes } from './itemTime';
import { collectAttachments, collectUserNotes, visibleDayItems } from './notesAttachments';
import { formatTempRange, resolveTripTemperatureUnit } from './temperatureUnit';
import {
  buildTripDayCards,
  enumerateStayDays,
  formatCardWeekdayDate,
  formatTripHeaderDate,
  visitContainsDate,
  type TripDayCardModel,
} from './tripDayCards';
import { estimateTileHeight, wrapPdfText } from './itineraryPdfLayout';
import { formatYmd, ymdFromUnknown } from './wallClock';

export interface ItineraryPdfWeather {
  highTempC: number;
  lowTempC: number;
}

export type PdfTile =
  | { kind: 'dayHeader'; lines: string[]; height: number }
  | { kind: 'hotel'; hotelName: string; lines: string[]; height: number }
  | { kind: 'cityHeader'; cityName: string; lines: string[]; height: number }
  | { kind: 'emptyActivities'; lines: string[]; height: number }
  | { kind: 'activity'; title: string; lines: string[]; height: number }
  | { kind: 'options'; cityName: string; lines: string[]; height: number }
  | { kind: 'notes'; lines: string[]; height: number }
  | { kind: 'tonight'; hotelName: string; lines: string[]; height: number };

export interface ItineraryPdfDay {
  dateYmd: string;
  continuationTitle: string;
  tiles: PdfTile[];
}

export interface ItineraryPdfModel {
  title: string;
  fileName: string;
  cover: {
    dateRange: string;
    summary: string;
    cities: string[];
  };
  days: ItineraryPdfDay[];
}

export function itineraryPdfFileName(title: string): string {
  const base =
    title
      .trim()
      .replace(/[\\/:*?"<>|]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || tripDetailEn.untitled;
  return `${base}_Itinerary.pdf`;
}

function withHeight<T extends { lines: string[] }>(entry: T): T & { height: number } {
  return { ...entry, height: estimateTileHeight(entry.lines.length) };
}

function linesOf(...parts: Array<string | null | undefined>): string[] {
  return parts.flatMap((part) => (part?.trim() ? wrapPdfText(part) : []));
}

function fullDateLabel(ymd: string): string {
  return formatYmd(ymd, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function activityItems(items: ItineraryItem[]): ItineraryItem[] {
  return visibleDayItems(items)
    .filter((item) => item.type !== 'accommodation')
    .sort((a, b) => {
      const byTime = compareItemTimes(a.time, b.time);
      if (byTime !== 0) return byTime;
      return a.title.localeCompare(b.title);
    });
}

function timeRangeLabel(items: ItineraryItem[]): string | null {
  const timed = items
    .map((item) => ({ item, minutes: itemTimeToMinutes(item.time) }))
    .filter((row): row is { item: ItineraryItem; minutes: number } => row.minutes != null);
  if (timed.length === 0) return null;
  timed.sort((a, b) => a.minutes - b.minutes);
  const start = formatItemTimeDisplay(timed[0].item.time);
  const end = formatItemTimeDisplay(timed[timed.length - 1].item.time);
  if (!start) return null;
  if (!end || start === end) return start;
  return `${start} – ${end}`;
}

function activityLines(item: ItineraryItem): string[] {
  const when = formatItemTimeDisplay(item.time);
  const title = when ? `${when}  ${item.title}` : item.title;
  const address = item.location?.address?.trim() || item.address?.trim() || '';
  const carrier = item.type === 'transport' ? item.provider?.trim() || item.metadata?.operator?.trim() : '';
  const number = item.type === 'transport' ? item.metadata?.number?.trim() : '';
  return linesOf(
    title,
    carrier ? `${dayViewEn.carrierLabel}: ${carrier}` : null,
    number ? `${dayViewEn.flightNumberLabel}: ${number}` : null,
    address || null,
    item.confirmationNumber?.trim()
      ? `${dayViewEn.conf} ${item.confirmationNumber.trim()}`
      : null,
    item.notes?.trim() || null
  );
}

function hotelCardLines(stay: TripHotelStay): string[] {
  const checkIn = formatTripHeaderDate(stay.checkInDate ?? null);
  const checkOut = formatTripHeaderDate(stay.checkOutDate ?? null);
  const dates = [checkIn ? `${dayViewEn.checkIn} ${checkIn}` : null, checkOut ? `${dayViewEn.checkOut} ${checkOut}` : null]
    .filter(Boolean)
    .join(' · ');
  return linesOf(stay.hotelName, stay.address, dates);
}

function itemsForCityDate(
  trip: Trip,
  cityId: string,
  dateYmd: string,
  claimUnscoped: { claimed: boolean }
): ItineraryItem[] {
  const collected: ItineraryItem[] = [];
  for (const day of trip.itineraryDays ?? []) {
    if (ymdFromUnknown(day.date) !== dateYmd) continue;
    const dayCity = day.cityId?.trim() ?? '';
    if (dayCity) {
      if (dayCity !== cityId) continue;
    } else if (claimUnscoped.claimed) {
      continue;
    } else {
      claimUnscoped.claimed = true;
    }
    collected.push(...(day.items ?? []));
  }
  return activityItems(collected);
}

function dayTiles(
  trip: Trip,
  dateYmd: string,
  cards: TripDayCardModel[],
  weatherFor?: (cityId: string, dateYmd: string) => ItineraryPdfWeather | null
): PdfTile[] {
  const stays = hotelStaysForDate(allTripHotelStays(trip), dateYmd);
  const claimUnscoped = { claimed: false };
  const groups = cards.map((card) => ({
    cityId: card.cityId,
    cityName: card.cityName,
    items: itemsForCityDate(trip, card.cityId, dateYmd, claimUnscoped),
  }));
  const showHeaders = shouldShowCitySectionHeaders(
    groupDayItemsByCity(
      groups.map((group) => ({ cityId: group.cityId, items: group.items })),
      trip.cityNames,
      dayViewEn.unknownCity
    )
  );
  const cityLabel =
    cards.length > 1 ? tripDetailEn.pdfCityCount(cards.length) : cards[0]?.cityName || dayViewEn.unknownCity;
  const dateLabel = formatCardWeekdayDate(dateYmd) ?? dateYmd;
  const weatherCity = cards[0];
  const weather = weatherCity ? weatherFor?.(weatherCity.cityId, dateYmd) : null;
  const weatherLabel = weather
    ? formatTempRange(
        weather.highTempC,
        weather.lowTempC,
        resolveTripTemperatureUnit(trip)
      )
    : null;

  const tiles: PdfTile[] = [
    withHeight({
      kind: 'dayHeader',
      lines: linesOf(`${dateLabel}, ${cityLabel}`, weatherLabel),
    }),
  ];

  for (const stay of stays) {
    if (!hotelStayIsCheckInOrOutOn(stay, dateYmd)) continue;
    tiles.push(
      withHeight({
        kind: 'hotel',
        hotelName: stay.hotelName,
        lines: hotelCardLines(stay),
      })
    );
  }

  for (const group of groups) {
    if (showHeaders) {
      const range = timeRangeLabel(group.items);
      tiles.push(
        withHeight({
          kind: 'cityHeader',
          cityName: group.cityName,
          lines: linesOf(group.cityName, range),
        })
      );
    }
    if (group.items.length === 0) {
      tiles.push(
        withHeight({
          kind: 'emptyActivities',
          lines: [tripDetailEn.pdfNoActivities],
        })
      );
    } else {
      for (const item of group.items) {
        tiles.push(
          withHeight({
            kind: 'activity',
            title: item.title,
            lines: activityLines(item),
          })
        );
      }
    }

    const options = [...(trip.cityOptions?.[group.cityId] ?? [])].sort((a, b) => {
      const order = (a.order ?? 0) - (b.order ?? 0);
      if (order !== 0) return order;
      return a.title.localeCompare(b.title);
    });
    const optionLines =
      options.length === 0
        ? [dayViewEn.options, dayViewEn.noOptions]
        : [
            dayViewEn.options,
            ...options.flatMap((option) =>
              linesOf(
                option.completed ? `✓ ${option.title}` : option.title,
                option.description
              )
            ),
          ];
    tiles.push(
      withHeight({
        kind: 'options',
        cityName: group.cityName,
        lines: optionLines,
      })
    );
  }

  const notes = collectUserNotes(trip, 'today', { dayDate: dateYmd });
  const attachments = collectAttachments(trip, 'today', { dayDate: dateYmd });
  const noteLines: string[] = [tripDetailEn.pdfNotesTitle];
  if (notes.length === 0) {
    noteLines.push(tripDetailEn.pdfNoNotes);
  } else {
    for (const note of notes) {
      noteLines.push(...linesOf(note.title ? (note.body ? `${note.title} – ${note.body}` : note.title) : note.body));
    }
  }
  if (attachments.length > 0) {
    noteLines.push(tripDetailEn.pdfAttachmentsTitle);
    for (const attachment of attachments) {
      noteLines.push(...wrapPdfText(attachment.attachment.fileName));
    }
  }
  tiles.push(withHeight({ kind: 'notes', lines: noteLines }));

  for (const stay of stays) {
    if (!hotelStayIsOvernightOn(stay, dateYmd)) continue;
    tiles.push(
      withHeight({
        kind: 'tonight',
        hotelName: stay.hotelName,
        lines: [tripDetailEn.pdfTonight(stay.hotelName)],
      })
    );
  }

  return tiles;
}

export function buildItineraryPdfModel(
  trip: Trip,
  options?: {
    weatherFor?: (cityId: string, dateYmd: string) => ItineraryPdfWeather | null;
  }
): ItineraryPdfModel {
  const title = trip.title?.trim() || tripDetailEn.untitled;
  const cards = buildTripDayCards(trip);
  const cities: string[] = [];
  for (const card of cards) {
    const name = card.cityName.trim();
    if (name && !cities.includes(name)) cities.push(name);
  }

  const dates: string[] = [];
  const seen = new Set<string>();
  for (const card of cards) {
    for (const ymd of enumerateStayDays(card.arrivalDate, card.departureDate)) {
      if (seen.has(ymd)) continue;
      seen.add(ymd);
      dates.push(ymd);
    }
  }

  const days: ItineraryPdfDay[] = dates.map((dateYmd) => {
    const dayCards = cards.filter((card) => visitContainsDate(card, dateYmd));
    const cityLabel =
      dayCards.length > 1
        ? tripDetailEn.pdfCityCount(dayCards.length)
        : dayCards[0]?.cityName || dayViewEn.unknownCity;
    const tiles = dayTiles(trip, dateYmd, dayCards, options?.weatherFor);
    return {
      dateYmd,
      continuationTitle: `${fullDateLabel(dateYmd)} — ${cityLabel}`,
      tiles,
    };
  });

  const activityTotal = days.reduce(
    (sum, day) => sum + day.tiles.filter((entry) => entry.kind === 'activity').length,
    0
  );
  const start = formatTripHeaderDate(trip.startDate);
  const end = formatTripHeaderDate(trip.endDate);
  const dateRange = start && end && start !== end ? `${start} - ${end}` : start || end || '—';

  return {
    title,
    fileName: itineraryPdfFileName(title),
    cover: {
      dateRange,
      summary: tripDetailEn.pdfSummaryCounts(days.length, cities.length, activityTotal),
      cities,
    },
    days: days.map(({ dateYmd, continuationTitle, tiles }) => ({
      dateYmd,
      continuationTitle,
      tiles,
    })),
  };
}
